/**
 * ============================================================
 * KEJJA OS — src/pages/admin/AdminHomesPage.tsx (v2)
 * ============================================================
 */

import { useState } from 'react';
import { Home, Search, ChevronLeft, ChevronRight, Users, ChevronRight as ChevronRightInd } from 'lucide-react';
import { useAdminHomes } from '@/hooks/useAdmin';
import HomeDetailDrawer from '@/components/admin/HomeDetailDrawer';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 25;

export default function AdminHomesPage() {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const { data, isLoading } = useAdminHomes({ limit: PAGE_SIZE, offset: page * PAGE_SIZE, search: search || undefined });

  const homes = data?.homes ?? [];
  const total = data?.total ?? 0;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold">Homes</h1>
            <p className="text-xs text-slate-400 mt-0.5">{total.toLocaleString()} total · click a row for members + activity</p>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search home name…"
              className="bg-slate-900 border border-slate-800 rounded-md pl-8 pr-3 h-9 text-sm text-slate-100 placeholder-slate-500 w-64 focus:outline-none focus:border-slate-600"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : (
          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-800">
                  <th className="p-3 font-medium">Home</th>
                  <th className="p-3 font-medium">Owner</th>
                  <th className="p-3 font-medium">Members</th>
                  <th className="p-3 font-medium">Country</th>
                  <th className="p-3 font-medium">Currency</th>
                  <th className="p-3 font-medium">Created</th>
                  <th className="p-3 font-medium w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {homes.map((h) => (
                  <tr key={h.id}
                    onClick={() => setSelected(h.id)}
                    className={cn(
                      'cursor-pointer hover:bg-slate-800/50 transition-colors',
                      h.deletedAt && 'opacity-30 line-through',
                    )}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded bg-emerald-500/15 text-emerald-400 flex items-center justify-center flex-shrink-0">
                          <Home className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-100 truncate">{h.name}</div>
                          {h.motto && <div className="text-slate-500 italic truncate">"{h.motto}"</div>}
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      {h.ownerName ? (
                        <div className="min-w-0">
                          <div className="text-slate-200 truncate">{h.ownerName}</div>
                          <div className="text-slate-500 truncate">{h.ownerEmail}</div>
                        </div>
                      ) : <span className="text-slate-500">—</span>}
                    </td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1 tabular-nums">
                        <Users className="w-3 h-3 text-slate-500" />{h.memberCount}
                      </span>
                    </td>
                    <td className="p-3 font-mono text-slate-400">{h.country ?? '—'}</td>
                    <td className="p-3 font-mono text-slate-400">{h.currencyCode}</td>
                    <td className="p-3 text-slate-400 whitespace-nowrap">
                      {new Date(h.createdAt).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </td>
                    <td className="p-3">
                      <ChevronRightInd className="w-3.5 h-3.5 text-slate-600" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-slate-400">
          <div>Page {page + 1} of {maxPage + 1}</div>
          <div className="flex gap-1">
            <button disabled={page === 0} onClick={() => setPage(page - 1)}
              className="px-2 py-1 rounded hover:bg-slate-800 disabled:opacity-30 flex items-center gap-1">
              <ChevronLeft className="w-3 h-3" /> Prev
            </button>
            <button disabled={page >= maxPage} onClick={() => setPage(page + 1)}
              className="px-2 py-1 rounded hover:bg-slate-800 disabled:opacity-30 flex items-center gap-1">
              Next <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      <HomeDetailDrawer homeId={selected} onClose={() => setSelected(null)} />
    </>
  );
}
